import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const API = `${typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL || ''}/api`;
const MAX_POLLS = 12;

export default function PaymentSuccessPage() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const [state, setState] = useState('polling'); // polling | paid | timeout | error
    const [polls, setPolls] = useState(0);

    const poll = useCallback(async () => {
        if (!sessionId) { setState('error'); return; }
        try {
            const res = await axios.get(`${API}/payments/status/${sessionId}`);
            if (res.data.payment_status === 'paid') { setState('paid'); return; }
            setPolls((p) => {
                if (p + 1 >= MAX_POLLS) { setState('timeout'); return p + 1; }
                setTimeout(poll, 2000);
                return p + 1;
            });
        } catch {
            setState('error');
        }
    }, [sessionId]);

    useEffect(() => { poll(); }, [poll]);

    return (
        <div className="min-h-screen bg-[#F2F5F2] flex items-center justify-center p-4" data-testid="payment-success-page">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-6">
                    <img src="/omniglide-logo.png" alt="OmniGlide" className="h-14 object-contain" />
                </div>
                <Card className="w-full border border-[#CBD5E1] shadow-none overflow-hidden">
                    {state === 'paid' && <div className="h-1.5 bg-gradient-to-r from-[#1B4E8E] to-[#2E9BC9]" />}
                    <CardContent className="p-8 text-center">
                        {state === 'polling' && (
                            <>
                                <Loader2 className="h-12 w-12 animate-spin text-[#1B4E8E] mx-auto mb-4" />
                                <h1 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Confermiamo il tuo abbonamento…</h1>
                                <p className="text-sm text-[#64748B] mt-2">Attendi qualche secondo, ci siamo quasi.</p>
                            </>
                        )}
                        {state === 'paid' && (
                            <>
                                <div className="w-16 h-16 rounded-full bg-[#EAF2FB] flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle2 className="h-9 w-9 text-[#1B4E8E]" />
                                </div>
                                <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Benvenuto in OmniGlide! 🎉</h1>
                                <p className="text-sm text-[#64748B] mt-2 mb-5">La tua <strong className="text-[#1B4E8E]">prova gratuita di 15 giorni</strong> è attiva. Ti abbiamo inviato una email con il riepilogo del piano.</p>
                                <div className="text-left bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 mb-6">
                                    <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-2">I prossimi passi</p>
                                    <ul className="space-y-1.5 text-sm text-[#0F172A]">
                                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E9BC9] shrink-0" /> Accedi con l'email e la password scelte</li>
                                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E9BC9] shrink-0" /> Completa i dati della tua azienda</li>
                                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E9BC9] shrink-0" /> Collega il gestionale e importa i clienti</li>
                                    </ul>
                                </div>
                                <Button asChild className="w-full bg-[#1B4E8E] hover:bg-[#143A6B]" data-testid="payment-success-login">
                                    <Link to="/login">Accedi e configura la tua azienda</Link>
                                </Button>
                            </>
                        )}
                        {(state === 'timeout' || state === 'error') && (
                            <>
                                <XCircle className="h-12 w-12 text-[#B71C1C] mx-auto mb-4" />
                                <h1 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Stiamo ancora elaborando</h1>
                                <p className="text-sm text-[#64748B] mt-2 mb-6">Il pagamento potrebbe richiedere qualche istante. Prova ad accedere tra poco: se hai completato il checkout, il tuo account sarà attivo.</p>
                                <Button asChild variant="outline" className="w-full border-[#1B4E8E] text-[#1B4E8E]" data-testid="payment-success-login">
                                    <Link to="/login">Vai al login</Link>
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
